import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import { marked } from "marked";
import TurndownService from "turndown";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Link2,
  ImagePlus,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Undo2,
  Redo2,
  RemoveFormatting,
  Moon,
  Sun,
  Eye,
  FileCode,
  Pencil,
  Keyboard,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { prepareRichHtmlForDisplay, sanitizeRichHtml } from "@/lib/sanitizeRichHtml";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FontSize } from "./tiptapFontSize.js";
import "./rich-text-editor.css";

const DEFAULT_MAX = 32000;

const FONT_SIZES = [
  { label: "Padrão", value: "" },
  { label: "12 px", value: "12px" },
  { label: "14 px", value: "14px" },
  { label: "16 px", value: "16px" },
  { label: "18 px", value: "18px" },
  { label: "20 px", value: "20px" },
  { label: "24 px", value: "24px" },
  { label: "28 px", value: "28px" },
];

const FONT_FAMILIES = [
  { label: "Padrão", value: "" },
  { label: "Inter", value: "Inter, system-ui, sans-serif" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: '"Times New Roman", Times, serif' },
  { label: "Courier New", value: '"Courier New", monospace' },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
];

function RichToolbar({ editor, onLinkClick, onImageClick }) {
  const [helpOpen, setHelpOpen] = useState(false);
  if (!editor) return null;

  const align = (a) => () => editor.chain().focus().setTextAlign(a).run();

  const fsAttr = editor.getAttributes("textStyle").fontSize || "";
  const fsSelectValue = fsAttr ? fsAttr : "__default__";

  const ffRaw = editor.getAttributes("textStyle").fontFamily || "";
  const ffAttr = String(ffRaw).replace(/['"]+/g, "").trim();
  const ffMatch = FONT_FAMILIES.find((f) => f.value && f.value.replace(/['"]+/g, "") === ffAttr);
  const ffSelectValue = ffMatch?.value || "__default__";

  return (
    <div
      role="toolbar"
      aria-label="Formatação do texto"
      className="flex flex-wrap items-center gap-0.5 border-b border-border/80 bg-muted/40 px-1.5 py-1.5 backdrop-blur-sm transition-colors sm:gap-1"
    >
      <div className="flex flex-wrap items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Desfazer (Ctrl+Z)"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Refazer (Ctrl+Shift+Z)"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo2 className="h-4 w-4" />
        </Button>
      </div>

      <Separator orientation="vertical" className="mx-0.5 h-6 hidden sm:block" />

      <div className="flex flex-wrap items-center gap-0.5">
        <Toggle
          size="sm"
          pressed={editor.isActive("bold")}
          aria-label="Negrito (Ctrl+B)"
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("italic")}
          aria-label="Itálico (Ctrl+I)"
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("underline")}
          aria-label="Sublinhado (Ctrl+U)"
          onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("strike")}
          aria-label="Tachado"
          onPressedChange={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-4 w-4" />
        </Toggle>
      </div>

      <Separator orientation="vertical" className="mx-0.5 h-6 hidden sm:block" />

      <div className="flex flex-wrap items-center gap-0.5">
        <Button
          type="button"
          variant={editor.isActive({ textAlign: "left" }) ? "secondary" : "ghost"}
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Alinhar à esquerda"
          aria-pressed={editor.isActive({ textAlign: "left" })}
          onClick={() => align("left")()}
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive({ textAlign: "center" }) ? "secondary" : "ghost"}
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Centralizar"
          aria-pressed={editor.isActive({ textAlign: "center" })}
          onClick={() => align("center")()}
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive({ textAlign: "right" }) ? "secondary" : "ghost"}
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Alinhar à direita"
          aria-pressed={editor.isActive({ textAlign: "right" })}
          onClick={() => align("right")()}
        >
          <AlignRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive({ textAlign: "justify" }) ? "secondary" : "ghost"}
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Justificar"
          aria-pressed={editor.isActive({ textAlign: "justify" })}
          onClick={() => align("justify")()}
        >
          <AlignJustify className="h-4 w-4" />
        </Button>
      </div>

      <Separator orientation="vertical" className="mx-0.5 h-6 hidden md:block" />

      <div className="flex flex-wrap items-center gap-1">
        <Select
          value={
            editor.isActive("heading", { level: 1 })
              ? "h1"
              : editor.isActive("heading", { level: 2 })
                ? "h2"
                : editor.isActive("heading", { level: 3 })
                  ? "h3"
                  : "p"
          }
          onValueChange={(v) => {
            const c = editor.chain().focus();
            if (v === "p") c.setParagraph().run();
            else if (v === "h1") c.setHeading({ level: 1 }).run();
            else if (v === "h2") c.setHeading({ level: 2 }).run();
            else if (v === "h3") c.setHeading({ level: 3 }).run();
          }}
        >
          <SelectTrigger className="h-8 w-[118px] text-xs" aria-label="Estilo de título">
            <SelectValue placeholder="Parágrafo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="p">Parágrafo</SelectItem>
            <SelectItem value="h1">
              <span className="flex items-center gap-2">
                <Heading1 className="h-3.5 w-3.5" /> Título 1
              </span>
            </SelectItem>
            <SelectItem value="h2">
              <span className="flex items-center gap-2">
                <Heading2 className="h-3.5 w-3.5" /> Título 2
              </span>
            </SelectItem>
            <SelectItem value="h3">
              <span className="flex items-center gap-2">
                <Heading3 className="h-3.5 w-3.5" /> Título 3
              </span>
            </SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={fsSelectValue}
          onValueChange={(v) => {
            if (v === "__default__") editor.chain().focus().unsetFontSize().run();
            else editor.chain().focus().setFontSize(v).run();
          }}
        >
          <SelectTrigger className="h-8 w-[92px] text-xs" aria-label="Tamanho da fonte">
            <SelectValue placeholder="Tamanho" />
          </SelectTrigger>
          <SelectContent>
            {FONT_SIZES.map((f) => (
              <SelectItem key={f.label} value={f.value || "__default__"}>
                {f.label}
              </SelectItem>
            ))}
            {fsAttr && !FONT_SIZES.some((f) => f.value === fsAttr) ? (
              <SelectItem value={fsAttr}>{fsAttr}</SelectItem>
            ) : null}
          </SelectContent>
        </Select>

        <Select
          value={ffSelectValue}
          onValueChange={(v) => {
            if (!v || v === "__default__") editor.chain().focus().unsetFontFamily().run();
            else editor.chain().focus().setFontFamily(v).run();
          }}
        >
          <SelectTrigger className="h-8 min-w-[108px] max-w-[140px] text-xs" aria-label="Família da fonte">
            <SelectValue placeholder="Fonte" />
          </SelectTrigger>
          <SelectContent>
            {FONT_FAMILIES.map((f) => (
              <SelectItem key={f.label} value={f.value || "__default__"}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator orientation="vertical" className="mx-0.5 h-6 hidden lg:block" />

      <div className="flex flex-wrap items-center gap-0.5">
        <label className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md hover:bg-muted" title="Cor do texto">
          <span className="sr-only">Cor do texto</span>
          <input
            type="color"
            className="h-6 w-6 cursor-pointer overflow-hidden rounded border-0 bg-transparent p-0"
            value={editor.getAttributes("textStyle").color || "#111827"}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </label>
        <label className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md hover:bg-muted" title="Cor de destaque (marca-texto)">
          <span className="sr-only">Cor de destaque</span>
          <input
            type="color"
            className="h-6 w-6 cursor-pointer overflow-hidden rounded border-0 bg-transparent p-0"
            value={editor.getAttributes("highlight").color || "#fef08a"}
            onChange={(e) => editor.chain().focus().setHighlight({ color: e.target.value }).run()}
          />
        </label>
      </div>

      <Separator orientation="vertical" className="mx-0.5 h-6 hidden lg:block" />

      <div className="flex flex-wrap items-center gap-0.5">
        <Toggle size="sm" pressed={editor.isActive("bulletList")} aria-label="Lista com marcadores" onPressedChange={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </Toggle>
        <Toggle size="sm" pressed={editor.isActive("orderedList")} aria-label="Lista numerada" onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </Toggle>
        <Toggle size="sm" pressed={editor.isActive("blockquote")} aria-label="Citação" onPressedChange={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="h-4 w-4" />
        </Toggle>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Inserir link" onClick={onLinkClick}>
          <Link2 className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Inserir imagem" onClick={onImageClick}>
          <ImagePlus className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Limpar formatação"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <RemoveFormatting className="h-4 w-4" />
        </Button>
        <Popover open={helpOpen} onOpenChange={setHelpOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Atalhos de teclado">
              <Keyboard className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(100vw-2rem,380px)] p-0" align="end">
            <div className="border-b px-3 py-2 text-sm font-medium">Atalhos</div>
            <ScrollArea className="max-h-72">
              <ul className="space-y-1.5 p-3 text-xs text-muted-foreground">
                <li>
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Ctrl</kbd> +{" "}
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">B</kbd> negrito
                </li>
                <li>
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Ctrl</kbd> +{" "}
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">I</kbd> itálico
                </li>
                <li>
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Ctrl</kbd> +{" "}
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">U</kbd> sublinhado
                </li>
                <li>
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Ctrl</kbd> +{" "}
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Z</kbd> desfazer
                </li>
                <li>
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Ctrl</kbd> +{" "}
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Shift</kbd> +{" "}
                  <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Z</kbd> refazer
                </li>
              </ul>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

const turndown = new TurndownService({ headingStyle: "atx", codeBlockStyle: "fenced" });

/**
 * Editor rico reutilizável (TipTap). Armazena HTML no `value` / `onChange`.
 */
export function RichTextEditor({
  id,
  label,
  value = "",
  onChange,
  placeholder = "Comece a escrever…",
  maxLength = DEFAULT_MAX,
  onAutoSave,
  className,
  minHeightClass = "min-h-[260px]",
}) {
  const [chromeDark, setChromeDark] = useState(false);
  const [tab, setTab] = useState("editor");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("https://");
  const [markdownDraft, setMarkdownDraft] = useState("");
  const [, setRteTick] = useState(0);
  const fileRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const emitTimer = useRef(null);
  const lastEmitted = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        history: { depth: 100 },
      }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph", "blockquote"] }),
      TextStyle,
      FontSize,
      FontFamily.configure({ types: ["textStyle"] }),
      Color.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
      Image.configure({ allowBase64: true, inline: true }),
      Placeholder.configure({ placeholder }),
      CharacterCount.configure({ limit: maxLength, mode: "textSize" }),
    ],
    [maxLength, placeholder]
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions,
    content: value || "",
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm sm:prose-base max-w-none focus:outline-none px-4 py-3",
          minHeightClass,
          "dark:prose-invert"
        ),
        "aria-labelledby": id ? `${id}-label` : undefined,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      clearTimeout(emitTimer.current);
      emitTimer.current = setTimeout(() => {
        lastEmitted.current = html;
        onChangeRef.current?.(html);
      }, 200);
      if (onAutoSave) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => onAutoSave(html), 4000);
      }
    },
  });

  useEffect(() => {
    if (!editor) return;
    const bump = () => setRteTick((n) => n + 1);
    editor.on("transaction", bump);
    return () => {
      editor.off("transaction", bump);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const cur = editor.getHTML();
    const incoming = value || "";
    if (incoming !== cur && incoming !== lastEmitted.current) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  useEffect(() => {
    marked.setOptions({ breaks: true, gfm: true });
  }, []);

  const openLinkDialog = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    setLinkUrl(typeof prev === "string" && prev ? prev : "https://");
    setLinkOpen(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      const u = /^https?:\/\//i.test(url) || url.startsWith("mailto:") ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange("link").setLink({ href: u }).run();
    }
    setLinkOpen(false);
  }, [editor, linkUrl]);

  const triggerImage = useCallback(() => fileRef.current?.click(), []);
  const onImageFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file || !editor) return;
      const reader = new FileReader();
      reader.onload = () => {
        const src = String(reader.result || "");
        if (src) editor.chain().focus().setImage({ src }).run();
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    },
    [editor]
  );

  const applyMarkdown = useCallback(() => {
    if (!editor) return;
    try {
      const raw = marked.parse(markdownDraft || "");
      const html = typeof raw === "string" ? raw : String(raw);
      const safe = sanitizeRichHtml(html);
      editor.commands.setContent(safe || "<p></p>", false);
      lastEmitted.current = safe;
      onChangeRef.current?.(safe);
      setTab("editor");
    } catch {
      /* ignore */
    }
  }, [editor, markdownDraft]);

  const syncMarkdownFromEditor = useCallback(() => {
    if (!editor) return;
    try {
      setMarkdownDraft(turndown.turndown(editor.getHTML()));
    } catch {
      setMarkdownDraft("");
    }
  }, [editor]);

  useEffect(() => {
    if (tab === "markdown" && editor) syncMarkdownFromEditor();
  }, [tab, editor, syncMarkdownFromEditor]);

  const chars =
    editor && typeof editor.storage?.characterCount?.characters === "function"
      ? editor.storage.characterCount.characters()
      : 0;

  return (
    <div className={cn("space-y-2", className)}>
      {label ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label id={id ? `${id}-label` : undefined} htmlFor={id}>
            {label}
          </Label>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1 text-xs"
              aria-pressed={chromeDark}
              onClick={() => setChromeDark((d) => !d)}
            >
              {chromeDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              {chromeDark ? "Claro" : "Escuro"}
            </Button>
          </div>
        </div>
      ) : null}

      <motion.div
        layout
        className={cn(
          "rich-text-editor-root overflow-hidden rounded-xl border border-border/90 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md",
          chromeDark && "dark"
        )}
        initial={{ opacity: 0.96, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="flex flex-col gap-0 border-b border-border/80 bg-muted/20 sm:flex-row sm:items-center sm:justify-between">
            <TabsList className="h-auto w-full justify-start rounded-none bg-transparent p-1 sm:w-auto">
              <TabsTrigger value="editor" className="gap-1.5 text-xs sm:text-sm">
                <Pencil className="h-3.5 w-3.5" /> Editar
              </TabsTrigger>
              <TabsTrigger value="markdown" className="gap-1.5 text-xs sm:text-sm">
                <FileCode className="h-3.5 w-3.5" /> Markdown
              </TabsTrigger>
              <TabsTrigger value="preview" className="gap-1.5 text-xs sm:text-sm">
                <Eye className="h-3.5 w-3.5" /> Preview
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2 px-2 pb-2 text-xs text-muted-foreground sm:pb-0 sm:pr-3">
              <span aria-live="polite">
                {chars.toLocaleString("pt-BR")} / {maxLength.toLocaleString("pt-BR")} caracteres
              </span>
              {onAutoSave ? <span className="hidden text-[10px] sm:inline">· auto-save após pausa</span> : null}
            </div>
          </div>

          <TabsContent value="editor" className="mt-0 focus-visible:outline-none">
            <RichToolbar editor={editor} onLinkClick={openLinkDialog} onImageClick={triggerImage} />
            <div className={cn("bg-background", chromeDark && "bg-slate-950")}>
              <EditorContent editor={editor} />
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImageFile} />
          </TabsContent>

          <TabsContent value="markdown" className="mt-0 space-y-3 p-3 sm:p-4">
            <p className="text-xs text-muted-foreground">
              Edite em Markdown e clique em &quot;Aplicar ao editor&quot; para converter para o documento rico.
            </p>
            <Textarea
              value={markdownDraft}
              onChange={(e) => setMarkdownDraft(e.target.value)}
              className={cn("min-h-[220px] font-mono text-sm", chromeDark && "bg-slate-900 text-slate-100")}
              spellCheck={false}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={applyMarkdown} className="gap-1">
                <Check className="h-4 w-4" /> Aplicar ao editor
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={syncMarkdownFromEditor}>
                Recarregar do editor
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="mt-0 p-3 sm:p-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={value?.slice(0, 40) || "empty"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className={cn(
                  "rounded-lg border border-border/60 bg-muted/30 p-4 prose prose-sm max-w-none dark:prose-invert",
                  chromeDark && "border-slate-700 bg-slate-900"
                )}
              >
                {value ? (
                  <div dangerouslySetInnerHTML={{ __html: prepareRichHtmlForDisplay(value) }} />
                ) : (
                  <p className="text-sm text-muted-foreground">Nada para pré-visualizar.</p>
                )}
              </motion.div>
            </AnimatePresence>
          </TabsContent>
        </Tabs>
      </motion.div>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Inserir ou editar link</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="rte-link-url">URL</Label>
            <Input
              id="rte-link-url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://…"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyLink();
                }
              }}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setLinkOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={applyLink}>
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
